import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { translations, COUNTRIES, CURRENCY_RATES, CURRENCY_SYMBOLS } from "./index";

const LocaleContext = createContext(null);

const SUPPORTED_LANGS = ["en", "es", "fr", "de", "pt", "it", "ar", "he"];
const RTL_LANGS = ["ar", "he"];

const detectInitialLang = () => {
  const stored = localStorage.getItem("luxe_lang");
  if (stored) return stored;
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(nav) ? nav : "en";
};

export function LocaleProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);
  const [country, setCountryState] = useState(() => localStorage.getItem("luxe_country") || "US");

  const isRTL = RTL_LANGS.includes(lang);

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang, isRTL]);

  const setLang = useCallback((l) => {
    localStorage.setItem("luxe_lang", l);
    setLangState(l);
  }, []);

  const setCountry = useCallback((c) => {
    localStorage.setItem("luxe_country", c);
    setCountryState(c);
    const info = COUNTRIES.find((x) => x.code === c);
    if (info) {
      localStorage.setItem("luxe_lang", info.lang);
      setLangState(info.lang);
    }
  }, []);

  const currency = COUNTRIES.find((c) => c.code === country)?.currency || "USD";

  const t = useCallback(
    (key, params) => {
      let s = translations[lang]?.[key] ?? translations.en[key] ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          s = s.split(`{${k}}`).join(String(v));
        }
      }
      return s;
    },
    [lang]
  );

  const fm = useCallback(
    (usd) => {
      if (currency === "USD") return null;
      const v = Number(usd) * (CURRENCY_RATES[currency] || 1);
      return `${CURRENCY_SYMBOLS[currency] || ""}${v.toFixed(2)}`;
    },
    [currency]
  );

  return (
    <LocaleContext.Provider value={{ lang, setLang, country, setCountry, currency, t, fm, isRTL }}>
      {children}
    </LocaleContext.Provider>
  );
}

export const useLocale = () => useContext(LocaleContext);
